/**
 * Home Page - Landing Page
 *
 * Modern, playful, professional landing page following UX_DESIGN.md.
 * Pattern: Landing Page (hero + sections + CTAs)
 */

import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Map,
  Sparkles,
  TrendingUp,
  ArrowRight,
  Check,
  Zap,
  Search,
  FileJson,
} from "lucide-react";
import { LandingLayout } from "@/components/layout/LandingLayout";
import { FeaturedQuizCard } from "@/components/quiz/FeaturedQuizCard";
import { getTopFeaturedQuizzes, type FeaturedQuizConfig } from "@/data/featuredQuizzes";
import { db } from "@/db/database";
import { generateQuizId } from "@/utils/idGenerator";
import { computeDatasetSignature } from "@/utils/quizConfig";
import { BookMascot } from "@/components/ui/BookMascot";

export function HomePage() {
  const navigate = useNavigate();

  // Handler for quick-start featured quizzes
  const handlePlayFeaturedQuiz = async (featured: FeaturedQuizConfig) => {
    try {
      const signature = generateQuizId();
      const datasetSource = { type: "dataset-url" as const, path: featured.datasetPath };
      const datasetSignature = computeDatasetSignature({
        datasetSource,
        datasetLabel: featured.datasetLabel,
      });

      // Look up geography from geojsonDatasets
      const { geojsonDatasets } = await import("@/data/geojsonDatasets");
      const datasetInfo = geojsonDatasets.find(d => d.path === featured.datasetPath);
      const geography = datasetInfo?.geography || featured.datasetLabel;

      // Create a quick-start quiz config matching V2 schema
      const quickStartConfig = {
        signature,
        title: featured.title,
        description: featured.description,
        category: featured.category,
        geography,
        mapQuizId: null,
        mapQuizName: null,
        datasetLabel: featured.datasetLabel,
        datasetSignature,
        datasetSource,
        promptKeys: [featured.promptKey],
        scopeKey: null,
        scopeBias: 50,
        repetitions: 1,
        maxAttemptsPerPrompt: 3,
        filters: [],
        excludedFeatureIds: [],
        eligibleCount: featured.featureCount,
        totalFeatures: featured.featureCount,
        granularityLevel: 0,
        isPublished: false,
        createdAt: new Date().toISOString(),
        lastUsedAt: new Date().toISOString(),
      };

      // Save to database for history
      await db.quiz_configs.add(quickStartConfig);

      // Navigate to quiz play
      navigate(`/quiz/play?signature=${quickStartConfig.signature}`);
    } catch (error) {
      console.error("Failed to start featured quiz:", error);
    }
  };

  const featuredQuizzes = getTopFeaturedQuizzes(6);

  return (
    <LandingLayout>
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            {/* Mascot */}
            <div className="mb-8">
              <BookMascot size="xl" />
            </div>

            {/* Badge */}
            <Badge className="mb-6 px-4 py-1.5 text-sm" variant="secondary">
              <Sparkles className="w-3.5 h-3.5 mr-1.5" />
              Interactive Geographic Learning Platform
            </Badge>

            {/* Heading */}
            <h1 className="text-5xl md:text-6xl font-extrabold mb-6 bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 bg-clip-text text-transparent">
              Master Geography Through Interactive Maps
            </h1>

            {/* Value Proposition */}
            <p className="text-lg md:text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
              Learn phone area codes, administrative divisions, and cities with interactive map quizzes.
              Track your progress globally, all offline-first and completely free.
            </p>

            {/* Quick Stats */}
            <div className="flex flex-wrap justify-center gap-6 text-sm text-muted-foreground mb-10">
              <div className="flex items-center gap-2">
                <Check className="w-4 h-4 text-green-500" />
                100% Free
              </div>
              <div className="flex items-center gap-2">
                <Check className="w-4 h-4 text-green-500" />
                Works Offline
              </div>
              <div className="flex items-center gap-2">
                <Check className="w-4 h-4 text-green-500" />
                No Ads
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Two Paths Section */}
      <section className="py-16 md:py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            {/* Section Header */}
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Choose Your Path
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Browse our catalogue of datasets or jump right into a featured quiz
              </p>
            </div>

            {/* Two Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto mb-8">
              {/* Path 1: Browse Catalogue */}
              <Card className="border-2 border-primary/50 hover:border-primary hover:shadow-xl transition-all cursor-pointer group">
                <CardContent className="p-8 text-center">
                  <div className="w-16 h-16 mx-auto mb-6 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                    <FileJson className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold mb-3">Browse Datasets</h3>
                  <p className="text-muted-foreground mb-6">
                    Explore our catalogue of geographic datasets. Filter by country,
                    topic, or search for specific regions. Preview maps before creating your quiz.
                  </p>
                  <Button
                    size="lg"
                    onClick={() => navigate("/geojson/browse")}
                    className="w-full group-hover:scale-105 transition-transform"
                  >
                    <Search className="w-5 h-5 mr-2" />
                    Explore Catalogue
                  </Button>
                </CardContent>
              </Card>

              {/* Path 2: Quick Start */}
              <Card className="border-2 hover:border-purple-500/50 hover:shadow-lg transition-all cursor-pointer group">
                <CardContent className="p-8 text-center">
                  <div className="w-16 h-16 mx-auto mb-6 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                    <Zap className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold mb-3">Quick Start</h3>
                  <p className="text-muted-foreground mb-6">
                    Jump into a ready-made quiz in one click. No setup needed,
                    just pick a featured quiz below and start learning immediately.
                  </p>
                  <Button
                    size="lg"
                    variant="outline"
                    onClick={() => {
                      document
                        .getElementById("featured-quizzes")
                        ?.scrollIntoView({ behavior: "smooth" });
                    }}
                    className="w-full group-hover:scale-105 transition-transform"
                  >
                    <ArrowRight className="w-5 h-5 mr-2" />
                    See Featured Quizzes
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Quizzes Section */}
      <section id="featured-quizzes" className="py-16 md:py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            {/* Section Header */}
            <div className="text-center mb-12">
              <Badge className="mb-4 px-4 py-1.5" variant="secondary">
                <Zap className="w-3.5 h-3.5 mr-1.5" />
                Quick Start
              </Badge>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Featured Quizzes
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Ready-to-play quizzes curated for learners at every level. No setup required!
              </p>
            </div>

            {/* Featured Quizzes Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {featuredQuizzes.map((quiz) => (
                <FeaturedQuizCard
                  key={quiz.id}
                  quiz={quiz}
                  onPlay={handlePlayFeaturedQuiz}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Key Features Section */}
      <section className="py-16 md:py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            {/* Section Header */}
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Powerful Learning Tools
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Everything you need to master geography
              </p>
            </div>

            {/* Features Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Feature 1: My Quizzes */}
              <Card className="hover:shadow-lg transition-all cursor-pointer group" onClick={() => navigate("/quiz/my-quizzes")}>
                <CardContent className="p-6 text-center">
                  <div className="w-12 h-12 mx-auto mb-4 bg-gradient-to-br from-amber-500 to-orange-500 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                    <Sparkles className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-lg font-semibold mb-2">
                    My Quizzes
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Create and manage your custom quizzes
                  </p>
                </CardContent>
              </Card>

              {/* Feature 2: Progress Tracking */}
              <Card className="hover:shadow-lg transition-all cursor-pointer group" onClick={() => navigate("/progress")}>
                <CardContent className="p-6 text-center">
                  <div className="w-12 h-12 mx-auto mb-4 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                    <TrendingUp className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-lg font-semibold mb-2">
                    Track Your Progress
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    View your knowledge map and statistics
                  </p>
                </CardContent>
              </Card>

              {/* Feature 3: Browse Datasets */}
              <Card className="hover:shadow-lg transition-all cursor-pointer group" onClick={() => navigate("/geojson/browse")}>
                <CardContent className="p-6 text-center">
                  <div className="w-12 h-12 mx-auto mb-4 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                    <Map className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-lg font-semibold mb-2">
                    Explore Maps
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Discover datasets from around the world
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-16 md:py-20 bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Ready to Start Learning?
            </h2>
            <p className="text-lg md:text-xl mb-8 opacity-90">
              Browse our catalogue of datasets or jump right into a featured quiz
            </p>
            <Button
              size="lg"
              variant="secondary"
              onClick={() => navigate("/geojson/browse")}
              className="text-lg px-8 py-6 rounded-xl shadow-lg hover:shadow-xl transition-all"
            >
              <Search className="w-5 h-5 mr-2" />
              Explore Datasets
            </Button>
          </div>
        </div>
      </section>
    </LandingLayout>
  );
}
